import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Add to cart button component
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const quantitySelector = form.querySelector('quantity-selector-component');
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return;
    }

    this.animateAddToCart();

    if (this.dataset.addToCartAnimation === 'true' && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    if (this.dataset.productVariantMedia) {
      preloadImage(this.dataset.productVariantMedia);
    }
  };

  #animateFlyToCart() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.dataset.productVariantMedia) return;

    const fly = document.createElement('fly-to-cart');
    fly.style.backgroundImage = `url(${this.dataset.productVariantMedia})`;
    fly.source = this.refs.addToCartButton;
    fly.destination = cartIcon;

    document.body.appendChild(fly);
  }

  animateAddToCart() {
    const btn = this.refs.addToCartButton;

    btn.classList.add('atc-added');
    clearTimeout(this.#animationTimeout);
    clearTimeout(this.#cleanupTimeout);

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        btn.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

customElements.define('add-to-cart-component', AddToCartComponent);

/**
 * Product form component
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  async #fetchAndUpdateCartQuantity() {
    const idInput = this.querySelector('input[name="id"]');
    if (!idInput?.value) return;

    const res = await fetch('/cart.js');
    const cart = await res.json();

    const item = cart.items.find(i => i.variant_id == idInput.value);
    const qty = item ? item.quantity : 0;

    this.querySelector('quantity-selector-component')?.setCartQuantity?.(qty);
  }

  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const formData = new FormData(form);
    const cfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'application/json' },
    })
      .then(r => r.json())
      .then(() => this.#fetchAndUpdateCartQuantity())
      .finally(() => cartPerformance.measureFromEvent('add:user-action', event));
  }

  #onCartUpdate = () => this.#fetchAndUpdateCartQuantity();

  #onVariantUpdate = async (event) => {
    this.refs.variantId.value = event.detail.resource?.id ?? '';
    await this.#fetchAndUpdateCartQuantity();
  };
}

customElements.define('product-form-component', ProductFormComponent);

/**
 * Fly to cart animation
 */
class FlyToCart extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => this.remove());
  }
}

customElements.define('fly-to-cart', FlyToCart);
